import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

const fmtQty = (qty) => qty ? `×${qty}` : '·  qty TBD';
const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
};

const STATUS_STYLES = {
  new:          { bg:`${G}18`,    color:'#059669',  label:'new' },
  in_progress:  { bg:'#FEF3C7',   color:'#92400E',  label:'in progress' },
  quoted:       { bg:'#DBEAFE',   color:'#1E40AF',  label:'quoted' },
  closed:       { bg:'#F1F5F9',   color:'#475569',  label:'closed' },
};

export default function QuotesPage({ getToken, showToast, navigate, buildPresentationFromQuote }) {
  const [quotes, setQuotes]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatus] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch]       = useState(() => {
    // Picks up a client-name hand-off from Dashboard's Recent activity (and
    // anywhere else that links here) so a quote row lands pre-filtered
    // instead of the full unfiltered list — there's no per-quote page to
    // deep-link to, so this is the closest honest equivalent.
    try {
      const raw = sessionStorage.getItem('pending_quotes_search');
      if (raw) { sessionStorage.removeItem('pending_quotes_search'); return raw; }
    } catch {}
    return '';
  });
  const [busyId, setBusyId]       = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getToken();
      const params = new URLSearchParams();
      if (showArchived) params.set('archived', 'true');
      if (search.trim()) params.set('q', search.trim());
      const r = await fetch(`/api/quotes?${params}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const data = await r.json();
      setQuotes(data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [getToken, showArchived, search]);

  useEffect(() => {
    const t = setTimeout(refresh, 250);
    return () => clearTimeout(t);
  }, [refresh]);

  const updateStatus = async (id, status) => {
    try {
      const t = await getToken();
      await api.withToken(t).updateQuote(id, status);
      setQuotes(p => p.map(q => q.id === id ? { ...q, status } : q));
      showToast('Updated');
    } catch (e) {
      showToast('Failed', 'error');
    }
  };

  const setArchived = async (id, archived) => {
    try {
      const t = await getToken();
      const r = await fetch(`/api/quotes/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type':'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({ archived }),
      });
      if (!r.ok) throw new Error('Server error');
      refresh();
      showToast(archived ? 'Archived' : 'Restored');
    } catch (e) {
      showToast('Failed: ' + e.message, 'error');
    }
  };

  const handleBuild = async (q) => {
    if (!buildPresentationFromQuote) {
      showToast('Build action not wired up — check App.jsx', 'error');
      return;
    }
    setBusyId(q.id);
    try {
      await buildPresentationFromQuote(q);
      if (q.status === 'new') {
        await updateStatus(q.id, 'in_progress');
      }
    } finally {
      setBusyId(null);
    }
  };

  const displayed = statusFilter === 'all' ? quotes : quotes.filter(q => q.status === statusFilter);

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16,flexWrap:'wrap',gap:12 }}>
        <div>
          <div style={{ fontSize:16,fontWeight:600,color:'#0F172A' }}>
            Quote Requests {showArchived && <span style={{ fontSize:12,color:'#94A3B8',fontWeight:400 }}>· archived</span>}
          </div>
          <div style={{ fontSize:13,color:'#64748B' }}>
            {showArchived ? 'Archived requests, searchable for repeat business.' : 'Submitted by clients from your storefront.'}
          </div>
        </div>
        <div style={{ display:'flex',gap:6,alignItems:'center',flexWrap:'wrap' }}>
          <button onClick={()=>setShowArchived(v=>!v)}
            style={{ background: showArchived ? BRAND.colors.dark : '#fff',
              color: showArchived ? '#fff' : '#475569',
              border:`1px solid ${showArchived ? BRAND.colors.dark : '#E2E8F0'}`,
              borderRadius:8,padding:'6px 14px',fontSize:12,cursor:'pointer',fontFamily:'inherit',fontWeight:500 }}>
            {showArchived ? '← Back to active' : '📦 View archived'}
          </button>
        </div>
      </div>

      <div style={{ display:'flex',gap:8,marginBottom:14,alignItems:'center',flexWrap:'wrap' }}>
        <div style={{ position:'relative',flex:1,minWidth:240,maxWidth:480 }}>
          <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'#94A3B8',pointerEvents:'none' }}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search by client name, company, email, or notes…"
            style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,
              padding:'9px 14px 9px 36px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box' }}/>
          {search && (
            <button onClick={()=>setSearch('')} aria-label="Clear search"
              style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                background:'none',border:'none',cursor:'pointer',color:'#94A3B8',fontSize:16,padding:'0 4px',fontFamily:'inherit' }}>×</button>
          )}
        </div>
        {!showArchived && (
          <div style={{ display:'flex',gap:5,flexWrap:'wrap' }}>
            {['all','new','in_progress','quoted','closed'].map(f => (
              <button key={f} onClick={()=>setStatus(f)}
                style={{ background: statusFilter===f ? BRAND.colors.dark : '#fff',
                  color: statusFilter===f ? '#fff' : '#64748B',
                  border:`1px solid ${statusFilter===f ? BRAND.colors.dark : '#E2E8F0'}`,
                  borderRadius:14,padding:'4px 11px',fontSize:11,cursor:'pointer',fontFamily:'inherit',
                  textTransform:'capitalize' }}>
                {f === 'all' ? 'All' : f.replace('_',' ')}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <div style={{ textAlign:'center',padding:40,color:'#94A3B8' }}>Loading...</div>}

      {!loading && displayed.length === 0 && (
        <div style={{ textAlign:'center',padding:'48px 24px',background:'#fff',borderRadius:12,border:'1px solid #E2E8F0' }}>
          <div style={{ fontSize:40,marginBottom:12 }}>{showArchived ? '📦' : (search ? '🔍' : '📬')}</div>
          <div style={{ fontSize:15,fontWeight:500,color:'#374151',marginBottom:6 }}>
            {search
              ? `No quotes match "${search}"`
              : showArchived
                ? 'No archived quotes'
                : statusFilter === 'all'
                  ? 'No quote requests yet'
                  : `No quotes with status "${statusFilter.replace('_',' ')}"`}
          </div>
          <div style={{ fontSize:13,color:'#94A3B8' }}>
            {showArchived
              ? 'Quotes you archive will appear here for future reference.'
              : statusFilter === 'all' && !search
                ? 'Once your storefront is live and clients submit requests, they appear here.'
                : 'Try adjusting your filters.'}
          </div>
          {!showArchived && statusFilter === 'all' && !search && (
            <button onClick={()=>navigate('storefront')}
              style={{ marginTop:16,background:G,border:'none',borderRadius:8,padding:'9px 20px',color:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
              Set up my storefront →
            </button>
          )}
        </div>
      )}

      {displayed.map(q => {
        const isBusy = busyId === q.id;
        const items = q.items || [];
        const statusStyle = STATUS_STYLES[q.status] || STATUS_STYLES.new;
        return (
          <div key={q.id} style={{ background:'#fff',borderRadius:12,border:'1px solid #E2E8F0',padding:20,marginBottom:14,
            opacity: q.archived ? 0.85 : 1 }}>
            <div style={{ display:'flex',justifyContent:'space-between',gap:16,marginBottom:14,flexWrap:'wrap' }}>
              <div style={{ display:'flex',gap:14,alignItems:'flex-start',minWidth:0,flex:1 }}>
                {q.client_logo && (
                  <img src={q.client_logo} alt="Client logo"
                    style={{ width:52,height:52,borderRadius:8,objectFit:'contain',
                      background:'#F8FAFC',border:'1px solid #E2E8F0',padding:4,flexShrink:0 }}/>
                )}
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:15,fontWeight:600,color:'#0F172A',lineHeight:1.3 }}>
                    {q.client_company || q.client_name}
                  </div>
                  <div style={{ fontSize:12,color:'#64748B',marginTop:3,wordBreak:'break-word' }}>
                    {q.client_name}
                    {q.client_email && <> · <a href={`mailto:${q.client_email}`} style={{ color:G,textDecoration:'none' }}>{q.client_email}</a></>}
                  </div>
                  <div style={{ fontSize:12,color:'#94A3B8',marginTop:2,display:'flex',gap:10,flexWrap:'wrap' }}>
                    {q.client_phone && <span>📞 {q.client_phone}</span>}
                    {q.event_date && <span>📅 Event: {fmtDate(q.event_date)}</span>}
                  </div>
                </div>
              </div>
              <div style={{ textAlign:'right',flexShrink:0 }}>
                {q.archived && (
                  <span style={{ background:'#F1F5F9',color:'#64748B',fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:8,marginRight:6,textTransform:'uppercase',letterSpacing:'0.5px' }}>
                    Archived
                  </span>
                )}
                <span style={{ background:statusStyle.bg,color:statusStyle.color,fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:10,textTransform:'capitalize' }}>
                  {statusStyle.label}
                </span>
                <div style={{ fontSize:11,color:'#94A3B8',marginTop:6 }}>
                  Received {fmtDate(q.created_at)}
                </div>
              </div>
            </div>

            {q.notes && q.notes.trim() && (
              <div style={{ fontSize:12,color:'#475569',background:'#F8FAFC',borderLeft:`3px solid ${G}`,
                borderRadius:'0 7px 7px 0',padding:'10px 14px',marginBottom:12,fontStyle:'italic',lineHeight:1.5 }}>
                "{q.notes}"
              </div>
            )}

            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6 }}>
                Requested ({items.length} item{items.length===1?'':'s'})
              </div>
              <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                {items.map((item, i) => {
                  const colorHex = item.color?.hex;
                  const colorName = item.color?.name;
                  const cleanHex = colorHex
                    ? (colorHex.startsWith('#') ? colorHex : `#${colorHex}`)
                    : null;
                  return (
                    <span key={i} style={{ background:'#F1F5F9',borderRadius:7,padding:'5px 10px',fontSize:12,color:'#475569',
                      display:'inline-flex',alignItems:'center',gap:6 }}>
                      <span>{item.name || item.product_name || 'Unnamed product'}</span>
                      <span style={{ color:'#94A3B8',fontSize:11 }}>{fmtQty(item.qty)}</span>
                      {colorName && (
                        <span style={{ display:'inline-flex',alignItems:'center',gap:4,
                          paddingLeft:6,borderLeft:'1px solid #CBD5E1',marginLeft:2 }}>
                          <span title={colorName} style={{ width:10,height:10,borderRadius:'50%',
                            background:cleanHex||'#ccc',border:'1px solid #E2E8F0',flexShrink:0 }}/>
                          <span style={{ fontSize:11,color:'#64748B' }}>{colorName}</span>
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>

            <div style={{ display:'flex',gap:8,flexWrap:'wrap',alignItems:'center' }}>
              {!q.archived && (
                <>
                  <button onClick={()=>handleBuild(q)} disabled={isBusy || items.length === 0}
                    style={{ background: isBusy || items.length === 0 ? '#CBD5E1' : G,
                      border:'none',borderRadius:7,padding:'8px 18px',color:'#fff',fontSize:12,fontWeight:600,
                      cursor: (isBusy || items.length === 0) ? 'not-allowed' : 'pointer',fontFamily:'inherit' }}>
                    {isBusy ? 'Loading…' : 'Build Presentation →'}
                  </button>
                  <a href={`mailto:${q.client_email}`}
                    style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:7,padding:'7px 16px',color:'#64748B',fontSize:12,textDecoration:'none',display:'inline-block',fontFamily:'inherit' }}>
                    Email Client
                  </a>
                </>
              )}
              <div style={{ marginLeft:'auto',display:'flex',gap:8,alignItems:'center' }}>
                {!q.archived && q.status !== 'closed' && (
                  <button onClick={()=>updateStatus(q.id,'closed')}
                    style={{ background:'none',border:'none',color:'#94A3B8',fontSize:12,cursor:'pointer',fontFamily:'inherit' }}>
                    Close
                  </button>
                )}
                {!q.archived && q.status === 'closed' && (
                  <button onClick={()=>updateStatus(q.id,'new')}
                    style={{ background:'none',border:'none',color:'#94A3B8',fontSize:12,cursor:'pointer',fontFamily:'inherit' }}>
                    Reopen
                  </button>
                )}
                <button onClick={()=>setArchived(q.id, !q.archived)}
                  style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:6,padding:'4px 10px',
                    fontSize:11,color:'#64748B',cursor:'pointer',fontFamily:'inherit' }}>
                  {q.archived ? '↺ Restore' : '📦 Archive'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
